import api from './apiService';
import type { AddArenaPayload } from '@/types/addArena';

export interface AddArenaResponse {
  success: boolean;
  message: string;
  data?: any;
}

class AddArenaService {
  async createField(payload: AddArenaPayload): Promise<AddArenaResponse> {
    try {
      console.log('Submitting arena field data:', payload);

      const response = await api.post<AddArenaResponse>('/fields/full', payload);

      if (response.data.success) {
        console.log('Arena field created successfully:', response.data);
        return response.data;
      } else {
        console.error('Failed to create arena field:', response.data);
        return {
          success: false,
          message: response.data.message || 'Failed to create arena field',
        };
      }
    } catch (error: any) {
      console.error('Error creating arena field:', error);

      // Handle different types of errors
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message,
        };
      } else if (error.response?.status === 422) {
        return {
          success: false,
          message: 'Validation error: Please check your input data',
        };
      } else if (error.response?.status === 401) {
        return {
          success: false,
          message: 'Unauthorized: Please login again',
        };
      } else {
        return {
          success: false,
          message: 'Network error: Please try again later',
        };
      }
    }
  }

  async createFieldWithImages(
    arenaSlug: string,
    payload: AddArenaPayload,
    images: File[] = []
  ): Promise<AddArenaResponse> {
    try {
      const form = new FormData();

      // Append scalar fields
      form.append('field_slug', payload.field_slug);
      form.append('name', payload.name);
      form.append('description', payload.description);
      form.append('type', payload.type);
      form.append('sport_category_slug', payload.sport_category_slug);
      // Append both keys to be safe: some backends expect 'capcity' (typo) and others 'capacity'
      form.append('capcity', String(payload.capacity ?? 0));
      form.append('capacity', String(payload.capacity ?? 0));
      form.append('is_dp_enabled', String(payload.is_dp_enabled));
      form.append('dp_per_session', String(payload.dp_per_session));
      form.append('price_per_session', String(payload.price_per_session));
      if (payload.is_active !== undefined) {
        form.append('is_active', String(payload.is_active));
      }

      // time_slots must be serialized as JSON string
      form.append('time_slots', JSON.stringify(payload.time_slots || []));

      // keep physical field fields if present
      form.append('physical_field_name', payload.physical_field_name || '');
      form.append('physical_field_slug', payload.physical_field_slug || '');

      form.append('surface_type', payload.surface_type || '');

      form.append('config', JSON.stringify(payload.config || {}));

      // Append images files
      images.forEach((file) => {
        form.append('images', file);
      });

      const url = `/arenas/${arenaSlug}/fields/full-with-images`;

      const response = await api.post<AddArenaResponse>(url, form);

      if (response.data.success) {
        return response.data;
      }

      return { success: false, message: response.data.message || 'Failed to create arena field' };
    } catch (error: any) {
      console.error('Error creating arena field with images:', error);
      if (error.response?.data?.message) {
        return { success: false, message: error.response.data.message };
      }
      return { success: false, message: 'Network error: Please try again later' };
    }
  }
}

export const addArenaService = new AddArenaService();
